import { Dialog, Portal, Progress, Tooltip } from "@ark-ui/react";
import {
  ALL_SIMPLE_INDEPENDENCE_LEVELS,
  type AssessmentInitializationFormInput,
  AssessmentInitializationFormSchema,
  DELETION_FLAG,
  ERROR_CODE,
  FIM_ITEM,
  FimItem,
  INDEPENDENCE_LEVEL_TYPE,
  SIMPLE_INDEPENDENCE_LEVEL,
  SimpleIndependenceLevelSchema,
  UserKey,
  fromFimItemToFimDomain,
  parse,
  simplifyIndependenceLevel,
} from "@packages/core";
import { useForm, useStore } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { LuImport } from "react-icons/lu";
import { type Merge } from "type-fest";
import * as v from "valibot";
import {
  ButtonStyle,
  Divider,
  DropDownList,
  PageName,
  TableStyle,
  TypographyStyle,
  dialogStyles,
  progressStyles,
  showError,
  showMessage,
  skeletonStyle,
  tooltipStyles,
} from "#components";
import {
  $alert,
  $confirm,
  registerIndependenceLevels,
  useGetErrorMessage,
  useLatestIndependenceLevelEntryQuery,
  useResidentInfiniteQuery,
  useTranslate,
} from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createFileRoute(
  "/_authenticated/facilities/$facilityKey/simple-independence-levels/registration",
)({
  component: Component,
});

function Component() {
  const { facilityKey } = Route.useParams();

  const formIdentifier = useId();
  const getErrorMessage = useGetErrorMessage();
  const navigate = useNavigate();
  const translate = useTranslate();
  const [progress, setProgress] = useState(100);

  const residentInfiniteQuery = useResidentInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });

  useEffect(
    function () {
      // 有効なデータが対象の場合、全て取得する。
      if (residentInfiniteQuery.hasNextPage && !residentInfiniteQuery.isFetching) {
        residentInfiniteQuery.fetchNextPage();
      }
    },
    [residentInfiniteQuery],
  );

  const residents = useMemo(
    function () {
      return (
        residentInfiniteQuery.data?.pages
          .flatMap(function (page) {
            return page.residents;
          })
          .sort(function (a, b) {
            return a.userKey < b.userKey ? -1 : a.userKey > b.userKey ? 1 : 0;
          }) ?? []
      );
    },
    [residentInfiniteQuery.data],
  );

  const parentRef = useRef<HTMLDivElement>(null);

  const schema = AssessmentInitializationFormSchema();
  const defaultValues = useMemo(
    function (): AssessmentInitializationFormInput {
      return {
        assessments: residents.map(function (resident) {
          return { userKey: resident.userKey };
        }),
      };
    },
    [residents],
  );

  const { Field, handleSubmit, ...form } = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      let output;
      try {
        console.debug("入力", input);
        output = parse(schema, input);
        console.debug("出力", output);
      } catch (error) {
        showError(error);
        setProgress(100);
        throw error;
      }

      setProgress(0);

      const independenceLevelEntries = [];
      for (const {
        simpleIndependenceLevelI,
        simpleIndependenceLevelL,
        simpleIndependenceLevelQ,
        simpleIndependenceLevelR,
        userKey,
      } of output.assessments) {
        if (
          !simpleIndependenceLevelI &&
          !simpleIndependenceLevelL &&
          !simpleIndependenceLevelQ &&
          !simpleIndependenceLevelR
        ) {
          continue;
        }

        if (
          !simpleIndependenceLevelI ||
          !simpleIndependenceLevelL ||
          !simpleIndependenceLevelQ ||
          !simpleIndependenceLevelR
        ) {
          // 自立度が断片的な場合はバリデーションによりありえない。
          throw new Error("入居者[" + userKey + "]の自立度の列が全て埋まっている必要があります。");
        }

        const resident = residents.find(function (resident) {
          return resident.userKey === userKey;
        });

        console.debug(
          "入居者[" +
            resident?.name +
            "(" +
            userKey +
            ")]の簡易自立度[" +
            simpleIndependenceLevelI +
            ", " +
            simpleIndependenceLevelL +
            ", " +
            simpleIndependenceLevelQ +
            ", " +
            simpleIndependenceLevelR +
            "]を登録します。",
        );

        independenceLevelEntries.push({
          simpleIndependenceLevels: {
            [FIM_ITEM.BED_CHAIR_WHEELCHAIR]: simpleIndependenceLevelI,
            [FIM_ITEM.WALK_WHEELCHAIR]: simpleIndependenceLevelL,
            [FIM_ITEM.PROBLEM_SOLVING]: simpleIndependenceLevelQ,
            [FIM_ITEM.MEMORY]: simpleIndependenceLevelR,
          },
          userKey,
        } as const);
      }

      try {
        await registerIndependenceLevels({
          independenceLevelEntries,
          type: INDEPENDENCE_LEVEL_TYPE.COMPLEMENTED,
        });
      } catch (error) {
        console.error(error);
        showMessage({
          body: translate("%Error." + ERROR_CODE.UNEXPECTED + ".0%"),
          color: "error",
          title: translate("%Error.0%"),
          icon: undefined,
          type: "toast",
        });
      }

      setProgress(100);

      await $alert({
        color: "success",
        content: translate("%AssessmentInitialization.1%"),
        title: translate("%Complete.0%"),
      });

      await navigate({
        ignoreBlocker: true,
        params: { facilityKey },
        to: "/facilities/$facilityKey/residents",
      });
    },
    onSubmitInvalid({ meta, value: input }) {
      console.warn("無効な入力", input, meta);
    },

    validators: { onSubmit: schema },
  });

  const assessments = useStore(form.store, (state) => state.values.assessments);
  const numberOfAssessments = assessments.length;

  {
    const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(form.store, (state) => state.isSubmitted);
    const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: translate("%Form.1%"),
              no: translate("%Stay.0%"),
              title: translate("%Form.0%"),
              yes: translate("%Leave.0%"),
            }))
          : false;
      },
      withResolver: true,
    });
  }

  const virtualizer = useVirtualizer({
    count: numberOfAssessments,
    estimateSize() {
      return 52;
    },
    getScrollElement() {
      return parentRef.current;
    },
    overscan: 5,
  });

  const dialogClasses = dialogStyles();
  const progressClasses = progressStyles();
  const tableClasses = TableStyle.raw({ foot: false });
  const tooltipClasses = tooltipStyles();
  const rowStyle = css.raw({
    gridTemplateColumns: "104px minmax(96px, 1fr) 40px repeat(4, 140px)",
  });

  const headCellClass = TypographyStyle({ color: "primary/60", ellipsis: true, fontWeight: 500 });

  return (
    <main
      className={css({
        display: "flex",
        flexDirection: "column",
        padding: { smDown: "16px", sm: "24px 32px" },
      })}
      data-maximized
    >
      <PageName>{translate("%RoomInformation.0%")}</PageName>

      <Dialog.Root open={progress !== 100}>
        <Portal>
          <Dialog.Backdrop className={dialogClasses.backdrop} />
          <Dialog.Positioner className={dialogClasses.positioner}>
            <Dialog.Content asChild>
              <Progress.Root className={progressClasses.root} value={progress}>
                <Progress.Label className={progressClasses.label}>
                  {translate("%Processing.0%")}
                </Progress.Label>
                <Progress.Track className={progressClasses.track}>
                  <Progress.Range className={progressClasses.range} />
                </Progress.Track>
                <Progress.ValueText className={progressClasses.valueText} />
              </Progress.Root>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <h1 className={css({ fontSize: "18px", fontWeight: 600 })}>
        {translate("%ResidentManagement.0%")}
      </h1>

      <div
        className={css({
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
          marginTop: "16px",
        })}
      >
        <div />

        <div
          className={css({
            alignItems: "center",
            display: "flex",
            gap: "8px",
          })}
        >
          <Link
            className={ButtonStyle({
              appearance: "outlined",
              borderRadius: "4px",
              color: "outlined",
              gap: "8px",
              fontSize: "14px",
              padding: "10px 16px",
            })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/simple-independence-levels/batch-registration"
          >
            <LuImport />
            <span>{translate("%Add.2%")}</span>
          </Link>

          <Divider length="28px" orientation="vertical" />

          <Link
            className={ButtonStyle({
              appearance: "outlined",
              borderRadius: "4px",
              color: "outlined",
              gap: "8px",
              fontSize: "14px",
              padding: "10px 16px",
            })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/residents"
          >
            {translate("%Cancel.0%")}
          </Link>

          <form.Subscribe
            selector={function (state) {
              return state.isSubmitting;
            }}
          >
            {function (submitting) {
              return (
                <button
                  className={ButtonStyle({
                    appearance: "contained",
                    borderRadius: "4px",
                    color: "primary",
                    gap: "8px",
                    fontSize: "14px",
                    padding: "10px 16px",
                  })}
                  disabled={submitting}
                  form={formIdentifier}
                  type="submit"
                >
                  {translate("%Save.0%")}
                </button>
              );
            }}
          </form.Subscribe>
        </div>
      </div>

      <div className={css({ flexGrow: 1, marginTop: "8px", overflowX: "auto", width: "100%" })}>
        <form
          className={css(
            tableClasses.root,
            TypographyStyle.raw({ fontSize: "14px", lineHeight: 1 }),
            { height: "stretch", maxHeight: "max-content" },
          )}
          id={formIdentifier}
          onSubmit={function (event) {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <div className={css(tableClasses.head)}>
            <div className={css(tableClasses.headRow, rowStyle)}>
              <div className={headCellClass}>{translate("%UserKey.0%")}</div>
              <div className={headCellClass}>{translate("%Name.0%")}</div>
              <div className={headCellClass}>{translate("%Sex.0%")}</div>
              {(
                [
                  FIM_ITEM.WALK_WHEELCHAIR,
                  FIM_ITEM.BED_CHAIR_WHEELCHAIR,
                  FIM_ITEM.PROBLEM_SOLVING,
                  FIM_ITEM.MEMORY,
                ] as const
              ).map(function (fimItem) {
                return (
                  <div
                    className={css(
                      TypographyStyle.raw({
                        color: "primary/60",
                        ellipsis: true,
                        fontSize: "14px",
                        lineHeight: 1,
                      }),
                      {
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      },
                    )}
                    key={fimItem}
                  >
                    <p className={TypographyStyle({ fontWeight: 500 })}>
                      {translate("%FimDomain." + fromFimItemToFimDomain(fimItem) + ".0%")}
                    </p>
                    <p className={TypographyStyle({ fontSize: "12px" })}>
                      {translate("%FimItem." + fimItem + ".0%")}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={css(tableClasses.virtualOuterBody)} ref={parentRef}>
            <div
              className={css(tableClasses.virtualInnerBody)}
              style={{ height: virtualizer.getTotalSize() + "px" }}
            >
              {virtualizer.getVirtualItems().map(function (virtualItem) {
                const assessment = assessments[virtualItem.index];
                const resident = residents.find(function (resident) {
                  return resident.userKey === assessment.userKey;
                });

                return (
                  <div
                    className={css(tableClasses.virtualBodyRow, rowStyle)}
                    data-index={virtualItem.index}
                    key={virtualItem.key}
                    ref={virtualizer.measureElement}
                    style={{
                      height: virtualItem.size + "px",
                      transform: "translateY(" + virtualItem.start + "px)",
                    }}
                  >
                    <div className={css(TypographyStyle.raw({ ellipsis: true, monospace: true }))}>
                      {assessment.userKey}
                    </div>
                    <div>{resident?.name}</div>
                    <div>{translate("%Sex." + resident?.sex + ".0%")}</div>
                    {(["L", "I", "Q", "R"] as const).map(function (key) {
                      let fimItem: FimItem;
                      switch (key) {
                        case "I":
                          fimItem = FIM_ITEM.BED_CHAIR_WHEELCHAIR;
                          break;
                        case "L":
                          fimItem = FIM_ITEM.WALK_WHEELCHAIR;
                          break;
                        case "Q":
                          fimItem = FIM_ITEM.PROBLEM_SOLVING;
                          break;
                        case "R":
                          fimItem = FIM_ITEM.MEMORY;
                          break;
                        default:
                          fimItem = FIM_ITEM.EATING;
                          break;
                      }

                      return (
                        <Field
                          key={key}
                          name={`assessments[${virtualItem.index}].simpleIndependenceLevel${key}`}
                        >
                          {function (field) {
                            const errorMessage = getErrorMessage(field.state.meta.errors.at(0));
                            return (
                              <Tooltip.Root
                                closeDelay={100}
                                openDelay={100}
                                positioning={{ placement: "top" }}
                              >
                                <Tooltip.Trigger asChild>
                                  <DropDownList
                                    className={cx(
                                      errorMessage
                                        ? css({
                                            backgroundColor: "destructive",
                                            color: "destructive-foreground",
                                          })
                                        : undefined,
                                      field.state.value ===
                                        SIMPLE_INDEPENDENCE_LEVEL.COMPLETE_ASSISTANCE &&
                                        css({ color: "destructive" }),
                                      field.state.value ===
                                        SIMPLE_INDEPENDENCE_LEVEL.INDEPENDENCE &&
                                        css({ color: "teal.600" }),
                                      !field.state.value && css({ color: "#91919A" }),
                                    )}
                                    onChange={function (event) {
                                      field.handleChange(
                                        event.target.value.length > 0
                                          ? v.parse(
                                              SimpleIndependenceLevelSchema(),
                                              Number.parseInt(event.target.value, 10),
                                            )
                                          : undefined,
                                      );
                                    }}
                                    value={field.state.value ?? ""}
                                  >
                                    <EmptyOption fimItem={fimItem} userKey={resident?.userKey} />
                                    {ALL_SIMPLE_INDEPENDENCE_LEVELS.map(
                                      function (simpleIndependenceLevel) {
                                        return (
                                          <option
                                            key={simpleIndependenceLevel}
                                            value={simpleIndependenceLevel}
                                          >
                                            {translate(
                                              "%SimpleIndependenceLevel." +
                                                simpleIndependenceLevel +
                                                ".0%",
                                            )}
                                          </option>
                                        );
                                      },
                                    )}
                                  </DropDownList>
                                </Tooltip.Trigger>
                                {errorMessage && (
                                  <Portal>
                                    <Tooltip.Positioner>
                                      <Tooltip.Content className={tooltipClasses.content}>
                                        <Tooltip.Arrow className={tooltipClasses.arrow}>
                                          <Tooltip.ArrowTip className={tooltipClasses.arrowTip} />
                                        </Tooltip.Arrow>
                                        <p className={TypographyStyle({ fontSize: "14px" })}>
                                          {errorMessage}
                                        </p>
                                      </Tooltip.Content>
                                    </Tooltip.Positioner>
                                  </Portal>
                                )}
                              </Tooltip.Root>
                            );
                          }}
                        </Field>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {residents.length === 0 &&
              (residentInfiniteQuery.isLoading ? (
                Array.from({ length: 5 }).map(function (_, index) {
                  return (
                    <div
                      className={css(tableClasses.bodyRow, rowStyle, { padding: "16px" })}
                      key={index}
                    >
                      <div className={css(skeletonStyle, { maxWidth: "96px" })} />
                      <div className={css(skeletonStyle, { maxWidth: "128px" })} />
                      <div />
                    </div>
                  );
                })
              ) : (
                <div className={css(tableClasses.bodyRow, { padding: "16px" })}>
                  <p className={css({ fontWeight: 500 })}>{translate("%NoItems.0%")}</p>
                </div>
              ))}
          </div>
        </form>
      </div>
    </main>
  );
}

type EmptyOptionProperties = Merge<
  React.ComponentProps<"option">,
  { children?: never; fimItem: FimItem; userKey?: UserKey; value?: never }
>;
function EmptyOption({ className, fimItem, userKey, ...properties }: EmptyOptionProperties) {
  const translate = useTranslate();

  const latestIndependenceLevelEntryQuery = useLatestIndependenceLevelEntryQuery(userKey);

  const assessment = latestIndependenceLevelEntryQuery.data;

  return (
    <option {...properties} className={className} value="">
      {latestIndependenceLevelEntryQuery.isLoading
        ? translate("%Loading.0%")
        : assessment
          ? translate(
              "%SimpleIndependenceLevel." +
                simplifyIndependenceLevel(assessment.independenceLevels[fimItem]) +
                ".0%",
            )
          : translate("%Empty.0%")}
    </option>
  );
}
